import React, { useState } from 'react';
import { Box, Text, TextInput, ActionIcon, Tooltip } from '@mantine/core';
import {
  IconFilter,
  IconWorldWww,
  IconPlus,
  IconTrash,
  IconChevronDown,
  IconChevronUp,
  IconCheck,
  IconPencil,
} from '@tabler/icons-react';
import { StepProps } from '../types';
import { useOnboardingStyles } from '../onboardingStyles';
import { useOnbColors } from '../onboardingColors';
import { StepVideo, FlowArrowDown } from '../common';
import pageStyles from '../Onboarding.module.css';

const FUNNEL_EXAMPLES = [
  ['Sales Pages', 'Checkout Pages'],
  ['Booking Pages', 'Upsell Pages'],
  ['Thank You Pages', 'Membership Pages'],
  ['Confirmation Pages', ''],
];

const ALMOST_DONE = [
  ['Track visitors', 'Create campaigns'],
  ['Launch optimization tests', 'Optimize your website'],
];

export function Step4Funnel({ data, update, errors, saving }: StepProps) {
  const { classes } = useOnboardingStyles();
  const col = useOnbColors();
  const [expanded, setExpanded] = useState(false);
  // While the funnel/pages are saving, lock all editing so nothing changes mid-request
  const busy = Boolean(saving);

  const pages = data.funnel.pages.length ? data.funnel.pages : [''];
  const extraPages = pages.slice(1);

  const setPages = (next: string[]) => update({ funnel: { ...data.funnel, pages: next } });
  const setLanding = (val: string) => setPages([val, ...extraPages]);
  const setExtra = (i: number, val: string) => setPages([pages[0], ...extraPages.map((p, idx) => (idx === i ? val : p))]);
  const addPage = () => setPages([...pages, '']);
  const removeExtra = (i: number) => setPages([pages[0], ...extraPages.filter((_, idx) => idx !== i)]);

  return (
    <Box className={`${classes.inner} ${pageStyles.stepEnter}`} style={{ maxWidth: 720 }}>
      <div className={classes.mark}>
        <IconFilter size={28} />
      </div>
      <h1 className={classes.title}>Create Your First Funnel</h1>
      <p className={classes.subtitle}>
        Tell Visiopt how visitors move through your website so we can accurately track every step,
        attribute conversions, and optimize your funnel.
      </p>

      <StepVideo
        duration="1:10"
        sublabel="A 70-second walkthrough of this step"
        modalTitle="How to build your first funnel"
        videoUrl="https://vimeo.com/483703741"
      />

      <div className={classes.fields}>
        {/* What is a funnel? (expandable) */}
        <div className={classes.panel} style={{ background: col.purpleBg }}>
          <div className={classes.infoTitle}>💡 What is a funnel?</div>
          <Text className={classes.qaText}>
            A funnel is simply the path visitors take through your website, from the landing page
            through every conversion step.
          </Text>
          <button type="button" className={classes.showToggle} onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Show less' : 'Show more'}
            {expanded ? <IconChevronUp size={15} /> : <IconChevronDown size={15} />}
          </button>
          {expanded && (
            <Box className={pageStyles.fadeIn} mt={12} pt={12} style={{ borderTop: '1px solid #E7E5F0' }}>
              <Text className={classes.qaText}>
                It starts with a Landing Page and includes every page a visitor can reach afterward,
                such as:
              </Text>
              <div className={classes.exGrid}>
                {FUNNEL_EXAMPLES.flat().filter(Boolean).map((e) => (
                  <div key={e} className={classes.qaItem}>
                    <span className={classes.qaBullet} />
                    {e}
                  </div>
                ))}
              </div>
              <Text className={classes.qaText} mt={12}>
                Whether your journey has 2 pages or 30 pages, Visiopt tracks the entire visitor
                experience.
              </Text>
            </Box>
          )}
        </div>

        {/* You're almost done */}
        <div className={classes.qaBox}>
          <div className={classes.infoTitle}>🎉 You're almost done</div>
          <Text className={classes.qaText}>
            Once you create your first funnel, your Visiopt account will be fully configured and
            you'll be ready to:
          </Text>
          <div className={classes.checkGrid}>
            {ALMOST_DONE.flat().map((c) => (
              <div key={c} className={classes.checkItem}>
                <IconCheck size={16} color={col.purpleText} /> {c}
              </div>
            ))}
          </div>
        </div>

        {/* Funnel name */}
        <TextInput
          size="md"
          radius="md"
          label="Funnel Name"
          classNames={{ label: classes.fieldLabel }}
          placeholder="Example: Main Sales Funnel"
          value={data.funnel.name}
          error={errors.funnelName}
          disabled={busy}
          onChange={(e) => update({ funnel: { ...data.funnel, name: e.currentTarget.value } })}
        />

        {/* Landing page URL */}
        <TextInput
          size="md"
          radius="md"
          label="Landing Page URL"
          description="Enter the first page visitors arrive on."
          classNames={{ label: classes.fieldLabel }}
          placeholder="https://example.com"
          icon={<IconWorldWww size={18} />}
          value={pages[0]}
          error={errors.landingUrl}
          disabled={busy}
          onChange={(e) => setLanding(e.currentTarget.value)}
        />

        {/* Additional funnel pages — growing journey */}
        <div>
          <div className={classes.sectionLabel}>Additional Funnel Pages</div>
          <div className={classes.fieldHelp}>
            Add pages in the order visitors move through your website. Visiopt uses this sequence to
            understand the complete customer journey.
          </div>

          <div className={classes.journeyWrap}>
            {/* Step 1 — landing (start) */}
            <div className={`${classes.journeyCard} ${classes.journeyCardStart}`}>
              <span className={classes.stepNum}>1</span>
              <Box style={{ flex: 1 }}>
                <div className={classes.stepEyebrow}>Step 1</div>
                <Text fz="sm" fw={700} c={col.textPrimary}>
                  Landing Page{' '}
                  <Text component="span" fz="sm" fw={400} c="dimmed">
                    {pages[0] || 'Your landing page URL'}
                  </Text>
                </Text>
              </Box>
              <span className={classes.startBadge}>Start</span>
            </div>

            {/* Added pages */}
            {extraPages.map((p, i) => (
              <React.Fragment key={i}>
                <FlowArrowDown />
                <div className={classes.journeyCard}>
                  <span className={classes.stepNum} style={{ background: col.dark ? '#3A3F4A' : '#111827' }}>
                    {i + 2}
                  </span>
                  <Box style={{ flex: 1 }}>
                    <div className={classes.stepEyebrow} style={{ color: col.textGray }}>
                      Step {i + 2}
                    </div>
                    <TextInput
                      variant="unstyled"
                      size="sm"
                      placeholder="https://example.com/next-page"
                      value={p}
                      disabled={busy}
                      error={errors[`page_${i + 1}`]}
                      onChange={(e) => setExtra(i, e.currentTarget.value)}
                      styles={{ input: { fontWeight: 600, color: col.textPrimary, height: 26, minHeight: 26 } }}
                    />
                  </Box>
                  <Tooltip label="Remove page" withArrow>
                    <ActionIcon variant="subtle" color="gray" radius="md" disabled={busy} onClick={() => removeExtra(i)}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                </div>
              </React.Fragment>
            ))}

            <FlowArrowDown />
            <button
              type="button"
              className={classes.addPageBtn}
              disabled={busy}
              style={busy ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              onClick={addPage}
            >
              <IconPlus size={16} /> Add Page
            </button>
          </div>
        </div>

        {/* [4d] Edit anytime + dynamic pages */}
        <div className={classes.cachingBox}>
          <div className={classes.noteHeading} style={{ color: col.amberText }}>
            <IconPencil size={15} /> EDIT ANYTIME
          </div>
          <Text fz={13.5} c={col.amberText}>
            Your funnel doesn't need to be perfect. You can edit, add, remove, or reorder pages at any
            time as your website evolves. Nothing you enter in Visiopt changes your current funnel flow
            or setup in any way. Dynamic or delayed-loading pages can be added here too — Visiopt keeps
            verifying them in the background and won't block your funnel.
          </Text>
        </div>
      </div>
    </Box>
  );
}
